import api from "../../../utils/api";
import type { Activity } from "./activityService";

// ============================================================================
// CRM — actividad REAL del equipo comercial y tiempo por prospecto (#1309).
//
// El panel de actividad mezclaba lo que hace el sistema (leads del landing,
// llamadas de la IA, sincronizaciones) con lo que hace el equipo. Aquí vive la
// misma regla que aplica el servidor (`crmActivityInsights.isSystemActivity`)
// para esconder lo del sistema por omisión, y el cliente de
// `GET /crm/activityInsights`: minutos por prospecto, promedio por etapa y
// tiempo invertido contra el monto cotizado.
// ============================================================================

/** Actores que no son una persona del equipo comercial (espejo del servidor). */
const SYSTEM_ACTORS = new Set(["sistema", "system", "ia", "ai", "bot", "cron", "webhook", "hubspot", "meta", "meta ads", "automatización", "automatizacion", "landing", "import"]);
const SYSTEM_ACTION_RE = /(sync|cron|webhook|import|backfill|hubspot|meta_ads|automation|workflow|bulk|scheduled|ingest|poll)/i;
const SYSTEM_SOURCES = new Set(["hubspot", "meta_ads", "import", "excel", "webhook", "cron", "system", "ai", "landing", "campaign", "nuvex", "wayak"]);

/** ¿Es un evento del sistema y no una actividad real del equipo comercial? */
export function isSystemActivity(a: Pick<Activity, "type" | "action" | "actor" | "meta"> | null | undefined): boolean {
  if (!a) return true;
  const meta = (a.meta ?? {}) as Record<string, unknown>;
  if (meta.workHours || a.type === "meeting") return false;
  // Un cambio de etapa hecho por una persona desde la consola (#1309).
  if (String(meta.origin ?? "").trim().toLowerCase() === "user") return false;
  const actor = String(a.actor ?? "").trim().toLowerCase();
  if (!actor || SYSTEM_ACTORS.has(actor)) return true;
  if (SYSTEM_ACTION_RE.test(String(a.action ?? ""))) return true;
  const source = String(meta.source ?? meta.origin ?? "").trim().toLowerCase();
  if (source && SYSTEM_SOURCES.has(source)) return true;
  return false;
}

/** ¿La actividad ya cuenta en Horas Trabajadas (#1308)? */
export const countsInWorkHours = (a: Pick<Activity, "meta"> | null | undefined): boolean => Boolean(a && a.meta && (a.meta as Record<string, unknown>).workHours);

export interface ProspectTime {
  id: string;
  key: string;
  kind: "lead" | "company" | "deal" | "name";
  name: string;
  stage: string;
  minutes: number;
  hours: number;
  meetings: number;
  touches: number;
  dealAmount: number;
  deals: number;
  currency: string;
}

export interface StageTime {
  stage: string;
  prospects: number;
  totalMinutes: number;
  meetings: number;
  avgMinutesPerProspect: number;
  avgHoursPerProspect: number;
}

export interface TimeVsAmount {
  id: string;
  key: string;
  name: string;
  stage: string;
  minutes: number;
  hours: number;
  amount: number;
  currency: string;
  meetings: number;
}

export interface ActivityInsights {
  byProspect: ProspectTime[];
  byStage: StageTime[];
  timeVsAmount: TimeVsAmount[];
  meta: { from: string; to: string; prospects: number; totalMinutes: number; totalHours: number; meetings: number; systemEventsExcluded: number };
}

export const EMPTY_INSIGHTS: ActivityInsights = {
  byProspect: [],
  byStage: [],
  timeVsAmount: [],
  meta: { from: "", to: "", prospects: 0, totalMinutes: 0, totalHours: 0, meetings: 0, systemEventsExcluded: 0 },
};

/** `GET /crm/activityInsights?from&to` (fechas locales YYYY-MM-DD). */
export async function fetchActivityInsights(params: { from: string; to: string }): Promise<ActivityInsights> {
  const res = await api.get("/crm/activityInsights", { params });
  // `response()` del backend envuelve en `{ meta, data: { message, data } }`.
  const outer = res?.data?.data ?? {};
  const data = (outer.data ?? outer) as Partial<ActivityInsights>;
  return {
    byProspect: Array.isArray(data.byProspect) ? data.byProspect : [],
    byStage: Array.isArray(data.byStage) ? data.byStage : [],
    timeVsAmount: Array.isArray(data.timeVsAmount) ? data.timeVsAmount : [],
    meta: { ...EMPTY_INSIGHTS.meta, ...(data.meta ?? {}) },
  };
}

/** Etiquetas en español de las etapas del embudo (negocios y estados de lead). */
export const STAGE_LABELS: Record<string, string> = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  agendado: "Agendado",
  calificado: "Calificado",
  propuesta: "Propuesta",
  negociacion: "Negociación",
  ganado: "Ganado",
  terminado: "Terminado",
  congelado: "Congelado",
  perdido: "Perdido",
  new: "Lead nuevo",
  contacted: "Lead contactado",
  qualified: "Lead calificado",
  unqualified: "Lead no calificado",
  converted: "Lead convertido",
  lost: "Lead perdido",
  prospect: "Empresa prospecto",
  customer: "Cliente",
  inactive: "Inactivo",
  sin_etapa: "Sin etapa",
};
export const stageLabel = (stage: string): string => STAGE_LABELS[stage] ?? stage;

/** Rangos del selector del panel: quincena, mes y 90 días (fechas locales). */
export type InsightsRange = "quincena" | "mes" | "90d";
export function rangeDates(range: InsightsRange, today = new Date()): { from: string; to: string } {
  const to = localIso(today);
  const days = range === "quincena" ? 15 : range === "mes" ? 30 : 90;
  const from = new Date(today);
  from.setDate(from.getDate() - (days - 1));
  return { from: localIso(from), to };
}
function localIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export const formatHours = (minutes: number): string => `${(Math.round((minutes / 60) * 10) / 10).toFixed(1)} h`;
export const formatMoney = (value: number, currency = "MXN"): string => {
  try {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${currency} ${Math.round(value).toLocaleString("es-MX")}`;
  }
};
